import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Save, XCircle } from "lucide-react";

import CustomButton from "@/components/desktop/button/CustomButton";
import CustomProcessingContent from "@/components/desktop/modal/CustomProcessingContent";

import styles from "./CustomModalForm.module.scss";

const SIZE_CLASS = {
	sm: styles.sizeSm,
	md: styles.sizeMd,
	lg: styles.sizeLg,
	xl: styles.sizeXl,
	xxl: styles.sizeXxl,
	full: styles.sizeFull,
};

const CustomModalForm = ({
	open,
	onClose,
	title,
	children,
	summary = null,
	footer = null,
	footerLeft = null,
	size = "md",
	width,
	closeOnBackdrop = false,
	closeOnEsc = true,
	asForm = false,
	textForm = "Guardar",
	showSave = true,
	onSubmit,
	initialFocusRef,
	ariaLabel = "Diálogo modal",
	isLoading = false,
	gridTable = true,
	processingMessage = "",
	processingDescription,
}) => {
	const panelRef = useRef(null);
	const lastActiveRef = useRef(null);
	const formIdRef = useRef(`cmf-${Math.random().toString(36).slice(2)}`);

	useEffect(() => {
		if (!open) return;

		lastActiveRef.current = document.activeElement;

		const toFocus =
			initialFocusRef?.current ||
			(panelRef.current ? panelRef.current.querySelector("input, select, textarea, button, [tabindex]:not([tabindex='-1'])") : null);

		if (toFocus) {
			toFocus.focus({ preventScroll: true });
		}

		const handleKey = (e) => {
			if (closeOnEsc && e.key === "Escape" && !isLoading) {
				if (typeof onClose === "function") onClose();
			}
		};
		document.addEventListener("keydown", handleKey);
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", handleKey);
			document.body.style.overflow = "";
			if (lastActiveRef.current && typeof lastActiveRef.current.focus === "function") {
				lastActiveRef.current.focus();
			}
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, closeOnEsc, initialFocusRef, isLoading]);

	if (!open) return null;

	const handleBackdrop = (e) => {
		if (!closeOnBackdrop) return;
		if (isLoading) return;
		if (e.target === e.currentTarget) {
			if (typeof onClose === "function") onClose();
		}
	};

	const showProcessingState = isLoading && Boolean(processingMessage);

	const defaultFooter = (
		<>
			<CustomButton variant="secondary" disabled={isLoading} startIcon={<XCircle size={16} />} onClick={onClose}>
				Cerrar
			</CustomButton>

			{showSave && (
				<>
					{asForm ? (
						<CustomButton variant="primary" loading={isLoading} startIcon={<Save size={16} />} type="submit" form={formIdRef.current}>
							{textForm}
						</CustomButton>
					) : (
						<CustomButton variant="primary" loading={isLoading} startIcon={<Save size={16} />} onClick={() => onSubmit?.()}>
							{textForm}
						</CustomButton>
					)}
				</>
			)}
		</>
	);

	let divStyle = { display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(12, minmax(0, 1fr))" };
	if (!gridTable) {
		divStyle = {};
	}

	const content = (
		<div className={`${styles.overlay} ${styles.show}`} onMouseDown={handleBackdrop} role="presentation">
			<div
				ref={panelRef}
				className={`${styles.panel} ${styles.panelIn} ${SIZE_CLASS[size] || styles.sizeMd}`}
				style={width ? { maxWidth: width } : undefined}
				role="dialog"
				aria-modal="true"
				aria-label={ariaLabel}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className={styles.header}>
					<div className={styles.titles}>{title ? <h2 className={styles.title}>{title}</h2> : null}</div>
					{!isLoading && (
						<button type="button" className={styles.close} onClick={onClose} aria-label="Cerrar">
							<X size={18} />
						</button>
					)}
				</div>

				{showProcessingState ? (
					<div className={styles.content}>
						<CustomProcessingContent message={processingMessage} description={processingDescription} />
					</div>
				) : (
					<>
						{summary && <div className={styles.summary}>{summary}</div>}

						{asForm ? (
							<form id={formIdRef.current} className={styles.content} onSubmit={onSubmit}>
								<div style={divStyle}>{children}</div>
							</form>
						) : (
							<div className={styles.content}>{children}</div>
						)}

						<div className={styles.footer}>
							<div className={styles.footerLeft}>{footerLeft}</div>
							<div className={styles.footerRight}>{footer ?? defaultFooter}</div>
						</div>
					</>
				)}
			</div>
		</div>
	);

	return createPortal(content, document.body);
};

export default CustomModalForm;
